// webPush.js
// ─────────────────────────────────────────────────────────────────
// Browser-side helpers for the iOS / desktop web-push pipeline.
// Service worker lives at /sw.js. On iOS, the user MUST add the site
// to their home screen ("Add to Home Screen") and open it from that
// icon for the push permission prompt to even appear — there's no way
// around that, it's an Apple rule. We surface that in the UI prompt.

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const VAPID_PUBLIC = process.env.REACT_APP_VAPID_PUBLIC_KEY || '';

const PROMPT_DISMISS_KEY = 'dl_push_prompt_dismissed_at';
const PROMPT_DISMISS_DAYS = 7;

/** Convert a base64url string into a Uint8Array for applicationServerKey. */
const urlBase64ToUint8Array = (b64) => {
  const padding = '='.repeat((4 - (b64.length % 4)) % 4);
  const base64 = (b64 + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
};

export const isPushSupported = () => {
  if (typeof window === 'undefined') return false;
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
};

// iOS only allows web push when the page was launched from a home-screen
// PWA install. `navigator.standalone` is set by Safari in that case.
export const isStandalonePWA = () => {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

export const isIOS = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent);
};

/** Ensure the SW is registered. Idempotent. Not gated on push support —
 * the SW also powers offline/instant caching, so every browser with SW
 * support gets it even if Push API is missing (in-app browsers, etc.). */
export const registerServiceWorker = async () => {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return null;
  try {
    const existing = await navigator.serviceWorker.getRegistration('/');
    if (existing) return existing;
    return await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  } catch (e) {
    console.warn('[push] sw register failed', e);
    return null;
  }
};

export const getCurrentSubscription = async () => {
  if (!isPushSupported()) return null;
  const reg = await registerServiceWorker();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
};

/**
 * Trigger permission prompt + create the push subscription + ship it
 * up to the backend. Returns the PushSubscription on success.
 */
export const subscribeForPush = async ({ userId } = {}) => {
  if (!isPushSupported()) throw new Error('Push not supported in this browser.');
  if (!VAPID_PUBLIC) throw new Error('VAPID public key missing on the frontend.');
  if (isIOS() && !isStandalonePWA()) {
    throw new Error('IOS_NEEDS_PWA');
  }
  const reg = await registerServiceWorker();
  if (!reg) throw new Error('Service worker registration failed.');

  let permission = Notification.permission;
  if (permission !== 'granted') {
    permission = await Notification.requestPermission();
  }
  if (permission !== 'granted') throw new Error('PERMISSION_DENIED');

  const existing = await reg.pushManager.getSubscription();
  const sub = existing || await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC),
  });

  const json = sub.toJSON();
  await fetch(`${API}/push/subscribe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      endpoint: json.endpoint,
      keys: json.keys,
      expirationTime: json.expirationTime || null,
      user_id: userId || null,
    }),
  });
  // Belt-and-suspenders: re-attach any orphan (user_id=null) subscription
  // to the now-authenticated user. Handles the race where the subscribe
  // POST fired before the React user object had loaded — without this
  // the founder push query never matched the device and pushes silently
  // failed to fire on the lock screen.
  try {
    await fetch(`${API}/push/claim-orphans`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
    });
  } catch (err) {
    // Backend may be offline / unreachable during a transient deploy.
    // Push registration is best-effort; log for dev visibility but
    // don't surface to the user.
    if (typeof console !== 'undefined') console.debug('[webPush] register-token failed:', err?.message || err);
  }
  return sub;
};

export const unsubscribeFromPush = async () => {
  const sub = await getCurrentSubscription();
  if (!sub) return;
  const endpoint = sub.endpoint;
  try { await sub.unsubscribe(); } catch (e) {
    if (typeof console !== 'undefined') console.warn('[webPush] unsubscribe() failed:', e);
  }
  try {
    await fetch(`${API}/push/unsubscribe`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ endpoint }),
    });
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('[webPush] /push/unsubscribe POST failed:', e);
  }
};

/** Lightweight heartbeat — frontend pings this while user is reading. */
export const sendReadingHeartbeat = async ({ slug, title, image, url, progress }) => {
  const sub = await getCurrentSubscription();
  if (!sub) return false;
  try {
    await fetch(`${API}/push/heartbeat`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        endpoint: sub.endpoint,
        slug, title, image: image || null, url,
        progress: Math.round(progress || 0),
      }),
      keepalive: true,
    });
    return true;
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('[webPush] heartbeat failed:', e);
    return false;
  }
};

export const dismissPushPrompt = () => {
  try { localStorage.setItem(PROMPT_DISMISS_KEY, String(Date.now())); } catch (e) {
    if (typeof console !== 'undefined') console.warn('[webPush] dismiss flag write failed:', e);
  }
};

export const shouldShowPushPrompt = () => {
  if (!isPushSupported()) return false;
  if (Notification.permission !== 'default') return false; // already decided
  if (isIOS() && !isStandalonePWA()) return false;          // can't even ask
  try {
    const ts = parseInt(localStorage.getItem(PROMPT_DISMISS_KEY) || '0', 10);
    if (ts && Date.now() - ts < PROMPT_DISMISS_DAYS * 86400 * 1000) return false;
  } catch (e) {
    if (typeof console !== 'undefined') console.warn('[webPush] dismiss flag read failed:', e);
  }
  return true;
};
